import { useState } from 'react';
import axios from 'axios';
import { MapPin, Send, Loader2, Mail, ExternalLink } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HAS_BACKEND = BACKEND_URL && BACKEND_URL.length > 0;

const locations = [
  {
    name: 'USA',
    address: '12110 Sunset Hills Rd, Ste 600 Reston, Virginia 20191, USA',
    mapSrc: 'https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d3102.614926542066!2d-77.36768542493179!3d38.95562464326787!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1shttps%3A%2F%2Fmaps.app.goo.gl%2F5F5qii6Qmsh9ukeq7!5e0!3m2!1sen!2sin!4v1724842454050!5m2!1sen!2sin',
  },
  {
    name: 'India',
    address: '40-5/3-9, Tikkle Rd, opp. Sneha Apartments, Shyam Nagar, Labbipet, Vijayawada, Andhra Pradesh 520010',
    mapSrc: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3825.488893394292!2d80.6474841741205!3d16.501398027725568!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a35fab0ab343e3f%3A0x244165c045610fe9!2sJhaishna%20Technologies%20Private%20Limited!5e0!3m2!1sen!2sin!4v1709802828807!5m2!1sen!2sin',
  },
  {
    name: 'Hong Kong',
    address: '16 Floor, Unit A, Dan 6, 2-6 Fui You Kok Street, Tsuen Wan, New Territories, Hong Kong',
    mapSrc: 'https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d12238.77848914491!2d114.10249143038598!3d22.37098478639803!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1s16%20Floor%2C%20Unit%20A%2C%20Dan%206%2C%202-6%20Fui%20You%20Kok%20Street%2C%20Tsuen%20Wan%2C%20New%20Territories%2C%20Hong%20Kong!5e0!3m2!1sen!2sin!4v1724843337015!5m2!1sen!2sin',
  },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    if (HAS_BACKEND) {
      // API mode: send to backend
      try {
        await axios.post(`${BACKEND_URL}/api/contact`, form);
        setStatus('success');
        setForm({ name: '', email: '', phone: '', message: '' });
      } catch (err) {
        console.error(err);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    } else {
      // Static mode (S3): open mailto
      const subject = encodeURIComponent(`Contact from ${form.name} - Jhaishna Website`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\nMessage:\n${form.message}`
      );
      window.location.href = `mailto:Info@jhaishna.com?subject=${subject}&body=${body}`;
      setStatus('success');
      setLoading(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-surface" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-16 text-center w-full">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="contact-page-title">
            Contact Us
          </h1>
          <p className="text-slate-300">Let's discuss how we can help transform your business</p>
        </div>
      </section>

      {/* Contact Form + Locations */}
      <section data-testid={CONTACT.section} className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">Ready to get started?</h2>
              <p className="text-slate-400 text-sm mb-8">Fill in the form below and our team will get back to you.</p>

              {status === 'success' && (
                <div data-testid={CONTACT.successMsg} className="mb-6 p-4 bg-green-900/30 border border-green-500/30 rounded-sm text-green-400 text-sm">
                  {HAS_BACKEND
                    ? 'Thank you! Your message has been sent successfully.'
                    : 'Your email client should open with the message. If not, please email us directly at Info@jhaishna.com'}
                </div>
              )}
              {status === 'error' && (
                <div data-testid={CONTACT.errorMsg} className="mb-6 p-4 bg-red-900/30 border border-red-500/30 rounded-sm text-red-400 text-sm">
                  Something went wrong. Please email us directly at{' '}
                  <a href="mailto:Info@jhaishna.com" className="underline">Info@jhaishna.com</a>
                </div>
              )}

              <form data-testid={CONTACT.form} onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Name</label>
                  <input
                    data-testid={CONTACT.nameInput}
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                  <input
                    data-testid={CONTACT.emailInput}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                    className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone</label>
                  <input
                    data-testid={CONTACT.phoneInput}
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter your number"
                    className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Message</label>
                  <textarea
                    data-testid={CONTACT.messageInput}
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Tell us about your project"
                    className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors resize-none"
                  />
                </div>
                <button
                  data-testid={CONTACT.submitBtn}
                  type="submit"
                  disabled={loading}
                  className="btn-amber rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>

              {/* Direct contact info */}
              <div className="mt-10 pt-8 border-t border-white/10 space-y-3">
                <a href="mailto:Info@jhaishna.com" className="flex items-center gap-3 text-sm text-slate-400 hover:text-amber transition-colors" data-testid="contact-direct-email">
                  <Mail className="w-4 h-4 text-amber" /> Info@jhaishna.com
                </a>
                <a href="mailto:hr@jhaishna.com" className="flex items-center gap-3 text-sm text-slate-400 hover:text-amber transition-colors">
                  <Mail className="w-4 h-4 text-amber" /> hr@jhaishna.com (Careers)
                </a>
              </div>
            </div>

            {/* Location Cards */}
            <div className="space-y-6">
              {locations.map((loc) => (
                <div key={loc.name} className="bg-surface border border-white/10 rounded-sm overflow-hidden card-hover" data-testid={`location-${loc.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="p-6 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="w-4 h-4 text-amber" />
                        <h3 className="font-heading text-lg font-bold text-white">{loc.name}</h3>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">{loc.address}</p>
                    </div>
                    <div className="map-container h-48 md:h-auto">
                      <iframe
                        src={loc.mapSrc}
                        width="100%"
                        height="100%"
                        style={{ border: 0, minHeight: '200px' }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Map - ${loc.name}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
